import html2pdf from 'html2pdf.js';
import { renderDocumentMarkdown } from '@/pages/core/documents/document-markdown';
import {
  getKnowledgeItemDisplayName,
  getMdItemText,
  resolveSourceKnowledgeItem,
} from './personal-knowledge';

const MD_TO_DOCX_PATH = '/server/md-docx?key=nbgz2024';

function triggerBrowserDownload(url, fileName) {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  if (fileName) a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function downloadTextAsFile(text, fileName, mime = 'text/markdown;charset=utf-8') {
  const blob = new Blob([text == null ? '' : String(text)], { type: mime });
  const url = URL.createObjectURL(blob);
  triggerBrowserDownload(url, fileName);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function safeFileStem(name) {
  const raw = String(name || '未命名文档').trim() || '未命名文档';
  return raw.replace(/[/\\?%*:|"<>]/g, '_').replace(/\.+$/, '');
}

function ensureMdFileName(name) {
  const stem = safeFileStem(name);
  if (/\.md$/i.test(stem) || /\.markdown$/i.test(stem)) return stem;
  return `${stem}.md`;
}

function ensureDocxFileName(name) {
  const stem = safeFileStem(name);
  if (/\.docx$/i.test(stem)) return stem;
  if (/\.doc$/i.test(stem)) return `${stem}x`;
  return `${stem}.docx`;
}

function ensurePdfFileName(name) {
  const stem = safeFileStem(name);
  if (/\.pdf$/i.test(stem)) return stem;
  return `${stem}.pdf`;
}

function getExportMarkdown(item, allItems) {
  const source = resolveSourceKnowledgeItem(item, allItems);
  if (!source) return '';
  const fromMd = getMdItemText(item, allItems);
  if (fromMd) return fromMd;
  if (source.markdown_content) return String(source.markdown_content).trim();
  return '';
}

function buildWordHtmlFromMarkdown(markdown) {
  const htmlBody = renderDocumentMarkdown(String(markdown || ''));
  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head><meta charset="utf-8"><title>Document</title></head>
<body>${htmlBody}</body>
</html>`;
}

function renderMarkdownToElement(markdown) {
  const wrapper = document.createElement('div');
  wrapper.className = 'mdmdt markdown-body';
  wrapper.style.padding = '24px';
  wrapper.style.background = '#fff';
  wrapper.innerHTML = renderDocumentMarkdown(String(markdown || ''));
  document.body.appendChild(wrapper);
  return wrapper;
}

function resolveMdToDocxUrl(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (data.url) return data.url;
  if (data.data && data.data.url) return data.data.url;
  if (data.result && data.result.url) return data.result.url;
  return '';
}

async function requestMdToDocx(api, { url, markdown, fileName }) {
  if (!api || typeof api.post !== 'function') {
    throw new Error('API 未就绪');
  }
  const endpoint = MD_TO_DOCX_PATH;
  let resp;
  if (url) {
    resp = await api.post(endpoint, { url });
  } else {
    const form = new FormData();
    const blob = new Blob([markdown == null ? '' : String(markdown)], { type: 'text/markdown;charset=utf-8' });
    form.append('file', blob, fileName || 'document.md');
    resp = await api.post(endpoint, form);
  }
  const payload = resp && resp.data;
  const docxUrl = resolveMdToDocxUrl(payload && payload.data != null ? payload.data : payload);
  if (!docxUrl) {
    const msg = (payload && (payload.message || payload.msg)) || '未返回 Word 下载地址';
    throw new Error(msg);
  }
  return docxUrl;
}

export function downloadKnowledgeItemMd(item, allItems) {
  const source = resolveSourceKnowledgeItem(item, allItems);
  if (!source) return { ok: false, message: '无法下载：内容不存在' };

  const displayName = getKnowledgeItemDisplayName(source, allItems);
  const markdown = getExportMarkdown(item, allItems);
  if (!markdown) return { ok: false, message: '无法下载：Markdown 内容为空' };

  downloadTextAsFile(markdown, ensureMdFileName(displayName));
  return { ok: true, message: '已开始下载 Markdown' };
}

export function downloadKnowledgeItem(item, allItems) {
  const source = resolveSourceKnowledgeItem(item, allItems);
  if (!source) return { ok: false, message: '无法下载：内容不存在' };

  const displayName = getKnowledgeItemDisplayName(source, allItems);

  if (source.type === 'file') {
    const url = source.content;
    if (!url) return { ok: false, message: '无法下载：文件地址为空' };
    triggerBrowserDownload(url, displayName);
    return { ok: true };
  }

  if (source.type === 'md' || source.type === 'text') {
    return downloadKnowledgeItemMd(item, allItems);
  }

  return { ok: false, message: '当前类型不支持下载' };
}

export async function copyKnowledgeItemContent(item, allItems) {
  const markdown = getExportMarkdown(item, allItems);
  if (!markdown) return { ok: false, message: '无法复制：内容为空' };

  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(markdown);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = markdown;
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!ok) throw new Error('copy failed');
    }
    return { ok: true, message: '已复制 Markdown' };
  } catch (err) {
    return { ok: false, message: '复制失败' };
  }
}

export async function copyKnowledgeItemWord(item, allItems) {
  const markdown = getExportMarkdown(item, allItems);
  if (!markdown) return { ok: false, message: '无法复制：内容为空' };

  const wordHtml = buildWordHtmlFromMarkdown(markdown);
  const plain = markdown;

  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([wordHtml], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      return { ok: true, message: '已复制（Word 格式）' };
    }
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(plain);
      return { ok: true, message: '已复制文本（浏览器不支持 Word 格式）' };
    }
    throw new Error('clipboard unavailable');
  } catch (err) {
    return { ok: false, message: '复制 Word 失败' };
  }
}

export async function exportKnowledgeItemPdf(item, allItems) {
  const source = resolveSourceKnowledgeItem(item, allItems);
  const markdown = getExportMarkdown(item, allItems);
  if (!markdown) return { ok: false, message: '仅支持 Markdown / 文本导出 PDF' };

  const displayName = getKnowledgeItemDisplayName(source, allItems);
  const el = renderMarkdownToElement(markdown);
  try {
    await html2pdf().set({
      margin: 1,
      filename: ensurePdfFileName(displayName),
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
    }).from(el).save();
    return { ok: true, message: 'PDF 导出已开始' };
  } catch (err) {
    return { ok: false, message: '导出 PDF 失败' };
  } finally {
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }
}

/** @deprecated 旧版 .doc 下载，保留兼容 */
export function exportKnowledgeItemWord(item, allItems) {
  const markdown = getExportMarkdown(item, allItems);
  if (!markdown) return { ok: false, message: '仅支持 Markdown / 文本导出 Word' };

  const source = resolveSourceKnowledgeItem(item, allItems);
  const displayName = getKnowledgeItemDisplayName(source, allItems);
  const wordHtml = buildWordHtmlFromMarkdown(markdown);
  downloadTextAsFile(wordHtml, `${safeFileStem(displayName)}.doc`, 'application/msword;charset=utf-8');
  return { ok: true };
}

export async function exportKnowledgeItemDocx(item, allItems, api) {
  const source = resolveSourceKnowledgeItem(item, allItems);
  const markdown = getExportMarkdown(item, allItems);
  if (!markdown) return { ok: false, message: '无法导出：Markdown 内容为空' };

  const displayName = getKnowledgeItemDisplayName(source, allItems);
  const fileName = ensureMdFileName(displayName);

  try {
    const contentUrl = source && source.content ? String(source.content).trim() : '';
    const canUseUrl = /^https?:\/\//i.test(contentUrl)
      && /\.(md|markdown)(\?|#|$)/i.test(contentUrl.split('?')[0]);

    const docxUrl = await requestMdToDocx(api, canUseUrl
      ? { url: contentUrl }
      : { markdown, fileName });
    triggerBrowserDownload(docxUrl, ensureDocxFileName(displayName));
    return { ok: true, message: 'Word 文档已开始下载' };
  } catch (err) {
    const msg = (err && err.response && err.response.data && (err.response.data.message || err.response.data.msg))
      || (err && err.message)
      || '导出 Word 失败';
    return { ok: false, message: msg };
  }
}
